/**
 * CreateHotelStep1 — 6-field form (v2.1 §24.2 · P0 user revision).
 * Under 60 seconds to complete. On success, navigates to Step 2.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const COMMON_TIMEZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Bangkok", "Asia/Jakarta",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "America/New_York", "America/Los_Angeles", "America/Chicago",
  "Australia/Sydney", "Africa/Cairo", "UTC",
];

const COMMON_CURRENCIES = [
  "INR", "USD", "EUR", "GBP", "AED", "SGD", "THB", "IDR", "JPY", "AUD",
];

const COMMON_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "bn", label: "Bengali" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "ar", label: "Arabic" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "de", label: "German" },
  { code: "th", label: "Thai" },
];

export default function CreateHotelStep1() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: "",
    brand_name: "",
    country: "India",
    timezone: "Asia/Kolkata",
    currency: "INR",
    default_language: "en",
    trial: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const payload = { ...f };
      if (!payload.brand_name) delete payload.brand_name;
      const { data } = await api.post("/platform/v1/hotels", payload);
      navigate(`/platform/hotels/${data.id}/administrator`, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(Array.isArray(detail) ? detail.map((x) => x.msg || JSON.stringify(x)).join(", ") : (detail || "Failed to create hotel"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="create-hotel-step1">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
          <Building2 className="w-3.5 h-3.5" />
          Step 1 of 2 · Create Hotel
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-2">Basic details</h1>
        <p className="text-slate-600 mt-2 text-sm">
          Under 60 seconds. Everything else is configured later in the Hotel Setup Wizard.
        </p>
      </header>

      {err && (
        <div
          className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-rose-800 text-sm flex items-start gap-2 mb-6"
          data-testid="create-hotel-step1-error"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        <Field label="Hotel Name" required>
          <input
            data-testid="create-hotel-step1-name"
            required
            value={f.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Oceáka Digha"
            className="pl-input"
          />
        </Field>

        <Field label="Brand" hint="Optional">
          <input
            data-testid="create-hotel-step1-brand"
            value={f.brand_name}
            onChange={(e) => set("brand_name", e.target.value)}
            placeholder="e.g. Oceáka Hospitality"
            className="pl-input"
          />
        </Field>

        <Field label="Country" required>
          <input
            data-testid="create-hotel-step1-country"
            required
            value={f.country}
            onChange={(e) => set("country", e.target.value)}
            className="pl-input"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Time Zone" required>
            <select
              data-testid="create-hotel-step1-timezone"
              required
              value={f.timezone}
              onChange={(e) => set("timezone", e.target.value)}
              className="pl-input"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </Field>

          <Field label="Currency" required>
            <select
              data-testid="create-hotel-step1-currency"
              required
              value={f.currency}
              onChange={(e) => set("currency", e.target.value)}
              className="pl-input"
            >
              {COMMON_CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Default Language" required>
          <select
            data-testid="create-hotel-step1-language"
            required
            value={f.default_language}
            onChange={(e) => set("default_language", e.target.value)}
            className="pl-input"
          >
            {COMMON_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </Field>

        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="create-hotel-step1-trial-label">
          <input
            data-testid="create-hotel-step1-trial"
            type="checkbox"
            checked={f.trial}
            onChange={(e) => set("trial", e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 bg-white text-amber-500 focus:ring-amber-500"
          />
          <span className="text-sm text-slate-700">Start as trial hotel</span>
        </label>

        <div className="pt-4 flex items-center gap-3">
          <button
            data-testid="create-hotel-step1-submit"
            type="submit"
            disabled={submitting || !f.name}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {submitting ? "Creating…" : "Continue"}
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => navigate("/platform/hotels")}
            className="px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      <style>{`
        .pl-input {
          display: block;
          width: 100%;
          border-radius: 0.5rem;
          border: 1px solid rgb(203 213 225);
          background: rgb(255 255 255);
          padding: 0.625rem 0.875rem;
          color: rgb(15 23 42);
          font-size: 0.875rem;
        }
        .pl-input:focus {
          outline: 2px solid rgb(245 158 11);
          border-color: transparent;
        }
        .pl-input::placeholder { color: rgb(148 163 184); }
      `}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-[13px] font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-amber-600">*</span>}
        {hint && <span className="text-slate-500 font-normal">({hint})</span>}
      </span>
      {children}
    </label>
  );
}
